import { Button, message, Select, Transfer } from 'antd';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FieldRegister from 'views/common/FieldRegister';
import TextField from 'views/common/TextField';
import Loading from 'views/common/Loading';
import API from 'api';
import { CheckOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import {
  checkHasAnyRoleOrPermission,
  PERMISSION,
  ROLE,
} from 'constants/permission';
import AccessControl from 'views/common/AccessControl';
import { useValidate, ValidateContext } from 'views/context/ValidateContext';
import { validateRequired } from 'utils/validate';

const validateCodeAuth = (v) => {
  if (v && !/[a-zA-Z0-9_]{5,}/.test(v)) {
    return 'Code at least 5 characters and must is alphanumeric or _';
  }
};

const transformBody = (state = {}) => {
  const { type, code, description, permissions, id } = state;
  if (type === 'ROLE') return { type, code, description, id, permissions };
  return { type, code, description, id };
};

const transformDetail = (data = {}) => {
  let { permissions = [] } = data;
  permissions = permissions.map((item) => item.id);
  return { ...data, permissions };
};

const CreateAuthorization = () => {
  const validate = useValidate('create-authorize');
  const [state, setState] = useState({ type: 'ROLE' });
  const { pathname } = useLocation();
  const isCreatePage = pathname.includes('create');
  const navigator = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [preload, setPreload] = useState({});
  const { authorizationId } = useParams();
  const { permissions, roles } = useSelector((state) => state.auth);

  const onChangeType = (type) => {
    setState((s) => ({ ...s, type }));
  };

  const onChangeField = (e, field) => {
    setState((s) => ({ ...s, [field]: e.target.value }));
  };

  const onChangePermissions = (options) => {
    setState((s) => ({ ...s, permissions: options }));
  };

  const onClickSave = async () => {
    const error = validate.validateFields();
    if (Object.values(error || {})?.length > 0) {
      return message.error('Authorization Invalid.');
    }
    setLoading(true);
    let res;
    if (isCreatePage) {
      res = await API.createNewAuthorization(transformBody(state));
    } else {
      res = await API.updateAuthorization(transformBody(state));
    }

    setLoading(false);

    if (res.status === 200) {
      message.success(
        isCreatePage
          ? 'Create authorization success'
          : 'Update authorization success'
      );
      return navigator('/authorization');
    }

    message.error(res.message);
  };

  const onCancel = () => {
    navigator('/authorization');
  };

  const preloadPermissions = async () => {
    const res = await API.getAllAuthorization({
      limit: 1000,
      type: 'PERMISSION',
    });

    if (res.status === 200) {
      setPreload({ permissions: res.data.data });
    }
  };

  const getDetail = async () => {
    const res = await API.getAuthorization(authorizationId);
    if (res.status === 200) {
      setState(transformDetail(res.data));
    }
  };

  const loadData = async () => {
    setLoading(true);
    if (!preload.permissions) await preloadPermissions();
    if (!isCreatePage) await getDetail();
    setLoading(false);
  };

  const filterTransfer = (input, option) => {
    const v = input.toLowerCase();
    return (
      option?.code?.toLowerCase()?.includes(v) ||
      option?.description?.toLowerCase()?.includes(v)
    );
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [authorizationId]);

  const permissionDisabled = !checkHasAnyRoleOrPermission(
    permissions,
    roles,
    [ROLE.SYSTEM_ADMIN],
    [
      isCreatePage
        ? PERMISSION.CREATE_AUTHORIZATION
        : PERMISSION.UPDATE_AUTHORIZATION,
    ]
  );

  return (
    <ValidateContext.Provider value={validate}>
      <div className="create-authorization" spellCheck={false}>
        {isLoading && <Loading />}
        <div className="create-authorization__title">
          {isCreatePage ? 'Create new authorizarion' : 'Edit authorization'}
        </div>
        <div className="create-authorization__block">
          <FieldRegister
            name="code"
            validate={[validateRequired('Code'), validateCodeAuth]}
            value={state.code}
            disabled={!isCreatePage}
            onChange={(e) => onChangeField(e, 'code')}
          >
            {({ value, onChange, onBlur, error, disabled }) => (
              <TextField
                value={value}
                label="Code"
                placeholder="Ender code"
                required
                disabled={disabled}
                helperText={error}
                onChange={onChange}
                onBlur={onBlur}
                status={error && 'error'}
              />
            )}
          </FieldRegister>
          <FieldRegister
            name="description"
            validate={validateRequired('Description')}
            value={state.description}
            disabled={permissionDisabled}
            onChange={(e) => onChangeField(e, 'description')}
          >
            {({ value, onChange, onBlur, error, disabled }) => (
              <TextField
                value={value}
                label="Description"
                placeholder="Enter description"
                required
                disabled={disabled}
                helperText={error}
                onChange={onChange}
                onBlur={onBlur}
                status={error && 'error'}
              />
            )}
          </FieldRegister>
          <TextField label="Type" required>
            <Select
              placeholder="Select type"
              style={{ width: '100%' }}
              onChange={onChangeType}
              value={state.type}
              disabled={!isCreatePage}
              size="large"
            >
              <Select.Option value="ROLE">Role</Select.Option>
              <Select.Option value="PERMISSION">Permission</Select.Option>
            </Select>
          </TextField>
          {state.type === 'ROLE' && (
            <TextField
              label="Permissions"
            >
              <Transfer
                className="transfer-component"
                dataSource={preload.permissions}
                showSearch
                titles={['Avaiable Permissions', 'Assigned Permissions']}
                targetKeys={state.permissions}
                onChange={onChangePermissions}
                render={(item) => `${item.code} (${item.description})`}
                rowKey={(item) => item.id}
                oneWay
                filterOption={filterTransfer}
                disabled={permissionDisabled}
              />
            </TextField>
          )}
        </div>
        <div className="create-authorization__footer">
          <Button onClick={onCancel}>Cancel</Button>
          <AccessControl
            permissions={[
              isCreatePage
                ? PERMISSION.CREATE_AUTHORIZATION
                : PERMISSION.UPDATE_AUTHORIZATION,
            ]}
            roles={[ROLE.SYSTEM_ADMIN]}
          >
            <Button
              icon={<CheckOutlined />}
              type="primary"
              onClick={onClickSave}
            >
              Save
            </Button>
          </AccessControl>
        </div>
      </div>
    </ValidateContext.Provider>
  );
};

export default CreateAuthorization;
